import { useEffect, useState } from 'react'
import { useAdmin } from '../context/AdminContext'
import { createAdminApi, APIKeyRecord } from '../services/adminApi'
import styles from './APIKeysPage.module.css'

export default function APIKeysPage() {
  const { apiKey } = useAdmin()
  const api = createAdminApi(() => apiKey)

  const [keys, setKeys] = useState<APIKeyRecord[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // Create form state
  const [newName, setNewName] = useState('')
  const [newLevel, setNewLevel] = useState<'admin' | 'standard'>('standard')
  const [creating, setCreating] = useState(false)

  // One-time reveal
  const [revealed, setRevealed] = useState<{ plaintext: string; name: string } | null>(null)
  const [copied, setCopied] = useState(false)

  async function load() {
    setLoading(true)
    setError('')
    try {
      const result = await api.listAPIKeys()
      setKeys(result ?? [])
    } catch (e) {
      setError(String(e))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (apiKey) load()
  }, [apiKey]) // eslint-disable-line react-hooks/exhaustive-deps

  async function handleCreate() {
    if (!newName.trim()) return
    setCreating(true)
    setError('')
    try {
      const result = await api.createAPIKey(newName.trim(), newLevel)
      setRevealed({ plaintext: result.plaintext, name: result.record.name })
      setNewName('')
      await load()
    } catch (e) {
      setError(String(e))
    } finally {
      setCreating(false)
    }
  }

  async function handleRevoke(id: string) {
    if (!confirm('Revoke this key? This cannot be undone.')) return
    setError('')
    try {
      await api.revokeAPIKey(id)
      await load()
    } catch (e) {
      setError(String(e))
    }
  }

  async function handleCopy() {
    if (!revealed) return
    await navigator.clipboard.writeText(revealed.plaintext)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  function formatDate(iso: string) {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1>API Keys</h1>
      </header>

      <div className={styles.content}>
        {error && <p className={styles.error}>{error}</p>}

        {revealed && (
          <div className={styles.reveal}>
            <p className={styles.revealWarning}>
              Copy this key now — it will <strong>not</strong> be shown again.
            </p>
            <p className={styles.revealName}>{revealed.name}</p>
            <div className={styles.revealRow}>
              <code className={styles.revealKey}>{revealed.plaintext}</code>
              <button className={styles.copyBtn} onClick={handleCopy}>
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <button className={styles.dismissBtn} onClick={() => setRevealed(null)}>Done</button>
          </div>
        )}

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Create New Key</h2>
          <div className={styles.createForm}>
            <input
              className={styles.input}
              placeholder="Key name"
              value={newName}
              onChange={e => setNewName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleCreate()}
            />
            <select
              className={styles.select}
              value={newLevel}
              onChange={e => setNewLevel(e.target.value as 'admin' | 'standard')}
            >
              <option value="standard">Standard</option>
              <option value="admin">Admin</option>
            </select>
            <button className={styles.createBtn} onClick={handleCreate} disabled={creating || !newName.trim()}>
              {creating ? 'Creating…' : 'Create'}
            </button>
          </div>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Existing Keys</h2>
          {loading ? (
            <p className={styles.dim}>Loading…</p>
          ) : keys.length === 0 ? (
            <p className={styles.dim}>No keys yet.</p>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Prefix</th>
                  <th>Level</th>
                  <th>Created</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {keys.map(k => (
                  <tr key={k.id} className={k.revokedAt ? styles.revoked : ''}>
                    <td>{k.name}</td>
                    <td><code>{k.keyPrefix}</code></td>
                    <td><span className={`${styles.badge} ${k.level === 'admin' ? styles.admin : styles.standard}`}>{k.level}</span></td>
                    <td>{formatDate(k.createdAt)}</td>
                    <td>{k.revokedAt ? <span className={styles.revokedBadge}>Revoked</span> : <span className={styles.activeBadge}>Active</span>}</td>
                    <td>
                      {!k.revokedAt && (
                        <button className={styles.revokeBtn} onClick={() => handleRevoke(k.id)}>Revoke</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </div>
    </div>
  )
}
